<template>
  <div>
    <b-navbar id="header" type="light" variant="light" sticky>
      <b-navbar-nav>
        <h4 class="mb-0">Персонал</h4>
      </b-navbar-nav>

      <b-navbar-nav class="ml-auto">
        <b-button id="table-settings" size="sm" variant="warning" class="mr-2" v-b-toggle.sidebar-fields>
          <font-awesome-icon icon="cog"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" variant="warning" noninteractive>
          Колонки
        </b-tooltip>
      </b-navbar-nav>
      <b-navbar-nav>
        <b-row>
          <b-col cols="auto">
            <b-btn v-if="canEditStaffs()" size="sm" variant="success" :disabled="isBusy" class="ml-1" @click="onCreate()">
              <b-spinner v-if="isBusy" small/>
              <font-awesome-icon icon="plus" class="mr-1"/>
              Новый
            </b-btn>
          </b-col>
        </b-row>
      </b-navbar-nav>
    </b-navbar>

    <filters-drawer
      :query.sync="filters.query"
      :is-busy="isBusy"
      :filter-tags="activeFilterTags"
      @remove-tag="removeFilterTag"
      @submit="onSearch"
      @clear="onClearFilters"
    >
      <b-col cols="12">
        <label class="small text-muted">Организация</label>
        <organization-select
          id="input-organization-select"
          ref="organization_select"
          v-bind:initial-id="filters.organization_id"
          v-on:onSelect="onSelectOrganization"
        />
      </b-col>
    </filters-drawer>

    <b-table
      id="staffs"
      ref="staffs"
      class="mb-0 text-small"
      selectable
      select-mode="single"
      selectedVariant="success"
      striped
      small
      responsive
      show-empty
      :empty-text="$t('message.common.emptyTable')"
      :busy="isBusy"
      :items="items"
      :fields="renderedFields"
      no-provider-filtering
      no-provider-paging
      no-provider-sorting
      no-local-sorting
      @sort-changed="sortChanged"
      @row-dblclicked="onRowClicked"
      sticky-header
      head-variant="light"
    >
      <template #table-busy>
        <div class="text-center text-success my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Загрузка</strong>
        </div>
      </template>

      <template #cell(name)="row">
        {{ getShortName(row.item.surname, row.item.name, row.item.patronymic) }}
      </template>

      <template #cell(migration_entry_date)="row">
        {{formatTableDate(row.item.migration_entry_date)}}
      </template>

      <template #cell(contract_add_date)="row">
        {{formatTableDate(row.item.contract_add_date)}}
      </template>

      <template #cell(patent_issue_date)="row">
        {{formatTableDate(row.item.patent_issue_date)}}
      </template>

      <template #cell(patent_expiration)="row">
        {{formatTableDate(row.item.patent_expiration)}}
      </template>

      <template #cell(active)="row">
        <font-awesome-icon v-if="!row.item.active" icon="times"/>
        <font-awesome-icon v-if="row.item.active" icon="check"/>
      </template>

      <template #cell(action)="row">
        <b-btn size="sm" variant="primary" @click="onEdit(row.item)">
          <font-awesome-icon icon="edit"/>
        </b-btn>
      </template>
    </b-table>

    <pagination-footer
      :total-rows="totalRows"
      :per-page.sync="perPage"
      @page-click="onPagination"
      @per-page-change="onPerPageChange"
    />

    <dynamic-tables
      storage-key="staffs_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />

  </div>
</template>

<script>

import staff from "../../network/staff";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
    import OrganizationSelect from "../Organizations/OrganizationSelect.vue";

export default {
  name: "Staffs",
  components: { PaginationFooter, FiltersDrawer, DynamicTables, OrganizationSelect },
  mixins: [
    staff,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  data() {
    return {
      items: [],
      fields: [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          class: 'staff-id',
          visible: true,
        },
        {
          key: 'name',
          label: "ФИО",
          sortable: true,
          class: 'staff-name',
          visible: true,
        },
        {
          key: 'citizenship',
          label: "Гражданство",
          sortable: false,
          class: 'staff-citizenship',
          visible: true,
        },
        {
          key: 'status',
          label: "Статус",
          sortable: false,
          class: 'staff-status',
          visible: true,
        },
        {
          key: 'organization_name',
          label: "Организация",
          sortable: false,
          class: 'staff-organization-name',
          visible: true,
        },
        {
          key: 'job_title_name',
          label: "Должность",
          sortable: false,
          class: 'staff-job-title-name',
          visible: true,
        },
        {
          key: 'contract_add_date',
          label: "Дата приёма",
          sortable: true,
          class: 'staff-contract-add-date',
          visible: true,
        },
        {
          key: 'inn',
          label: "ИНН",
          sortable: false,
          class: 'staff-inn',
          visible: true,
        },
        {
          key: 'snils',
          label: "СНИЛС",
          sortable: false,
          class: 'staff-snils',
          visible: true,
        },
        {
          key: 'patent_number',
          label: "Патент / ВНЖ",
          sortable: false,
          class: 'staff-patent-number',
          visible: true,
        },
        {
          key: 'patent_issue_date',
          label: "Дата выдачи патента",
          sortable: false,
          class: 'staff-patent-issue-date',
          visible: true,
        },
        {
          key: 'patent_expiration',
          label: "Дата окончания патента",
          sortable: true,
          class: 'staff-patent-expiration',
          visible: true,
        },
        {
          key: 'active',
          label: "Активный",
          sortable: false,
          class: 'staff-active',
          visible: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: "Действия",
        class: 'staff-action'
      },
      isBusy: false,
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        name: ''
      },
    }
  },
  async created() {
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.organization_id > 0) {
        this.filters.organization_id = query.organization_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.getItems();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.organization_id !== defaults.organization_id) {
        tags.push({ key: 'organization', label: `Организация`});
      }

      return tags;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        organization_id: this.filters.organization_id,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getStaffs(data);
      this.isBusy = false;

      if (result.data == null) {
        this.items = [];
        this.totalRows = 0;
        const mess = "Что-то пошло не так. Попробуйте обновить страницу и повторить действие."
        this.showError(mess);
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;

      this.items.forEach(function (item) {
        let variants = {};
        let today = new Date().toISOString().slice(0, 10);
        let plusTwoWeeks = new Date();
          plusTwoWeeks.setDate(plusTwoWeeks.getDate() + 14);
        let twoWeeks = new Date(plusTwoWeeks).toISOString().slice(0, 10);

        if (item.patent_expiration < today) {
          variants.patent_expiration = "danger";
        } else if (item.patent_expiration < twoWeeks) {
          variants.patent_expiration = "warning";
        } else {
          variants.patent_expiration = "null";
        }

        item._cellVariants = variants;
      }.bind(this));
    },
    onCreate() {
      if (this.hasPermission([PERM.STAFF_EDIT])) {
        this.$router.push({name: 'StaffAdd'}).catch(()=>{});
      }
    },
    onEdit(item) {
      if (this.hasPermission([PERM.STAFF_EDIT])) {
        this.$router.push({name: 'StaffEdit', params: { id: item.id }}).catch(()=>{});
      }
    },
    // Filters
    removeFilterTag(tag) {
      if (tag.key === 'organization') {
        this.$refs.organization_select.onClear();
        this.filters.organization_id = 0;
      }
      this.onSearch();
    },
    getDefaultFilters() {
      return {
        query: '',
        organization_id: 0,
      }
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }

      this.filters = filters;
      this.$refs.organization_select.onClear();

      this.onSearch();
    },
    onSelectOrganization(id) {
      this.filters.organization_id = id;
    },
    onRowClicked(item) {
      this.onEdit(item);
    },
    getShortName(surname, name, patronymic) {
      const firstCharName = name.slice(0,1);
      const firstCharPatronymic = patronymic.slice(0,1);
      if (firstCharPatronymic === '') {
        return `${surname} ${firstCharName}.`;
      }
      return `${surname} ${firstCharName}. ${firstCharPatronymic}.`;
    },
    // Permissions
    canEditStaffs() {
      return this.hasPermission([PERM.STAFF_EDIT]);
    }
  }
}
</script>

<style>
.staff-id {
  padding-left: 10px !important;
  width: 60px;
  text-align: center;
}

.staff-active {
  text-align: center;
}

.staff-action {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

#staffs td {
  vertical-align: middle !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Staff/Staffs.vue